import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChevronUp, ChevronDown, Check, Pencil, X, FolderTree, ImagePlus, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAIN_CATS = [
  { id: "alzheimer", label: "Te souviens-tu ?" },
  { id: "autisme", label: "Mon petit monde" },
  { id: "aphasie", label: "Dis-moi" },
  { id: "sensoriels", label: "Éveil des sens" },
  { id: "routine", label: "Au fil du jour" },
];

export function SubcategoryManager({ onChanged }) {
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("alzheimer");
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/game-categories");
      setCategories(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = categories.find((c) => c.id === active);
  const subs = current?.subcategories || [];

  const refresh = async () => { await load(); onChanged && onChanged(); };

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/subcategories", { category: active, name: newName.trim() });
      setNewName("");
      await refresh();
      toast.success("Sous-catégorie ajoutée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ajout impossible.");
    } finally { setBusy(false); }
  };

  const rename = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/admin/subcategories/${id}`, { name: editName.trim() });
      setEditingId(null);
      await refresh();
      toast.success("Renommée");
    } catch { toast.error("Modification impossible."); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/subcategories/${id}`);
      await refresh();
      toast.success("Supprimée");
    } catch { toast.error("Suppression impossible."); }
  };

  const move = async (idx, dir) => {
    const arr = [...subs];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try {
      await api.post("/admin/subcategories/reorder", { category: active, ordered_ids: arr.map((s) => s.id) });
      await refresh();
    } catch { toast.error("Réorganisation impossible."); }
  };

  const setImage = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/subcategories/${id}`, { image: `${BACKEND}${data.url}` });
      await refresh();
      toast.success("Image ajoutée");
    } catch { toast.error("Échec de l'envoi de l'image."); }
    finally { setUploadingId(null); }
  };

  return (
    <Card className="border-gray-200 mb-8" data-testid="subcategory-manager">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <FolderTree className="w-5 h-5 text-emerald-700" />
          <h2 className="font-serif-display text-xl font-normal text-gray-800">Catégories & sous-catégories de jeux</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-6">
          <div>
            <Label>Catégorie principale</Label>
            <Select value={active} onValueChange={(v) => { setActive(v); setEditingId(null); }}>
              <SelectTrigger className="mt-1" data-testid="subcat-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MAIN_CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sous-catégories ({subs.length})</Label>
            <div className="mt-1 flex gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="Nouvelle sous-catégorie" data-testid="subcat-new-input" />
              <Button onClick={add} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 shrink-0" data-testid="subcat-add-button">
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {subs.length === 0 && <p className="text-sm text-gray-400">Aucune sous-catégorie. Ajoutez-en une.</p>}
              {subs.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2" data-testid={`subcat-row-${s.id}`}>
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="text-gray-400 disabled:opacity-30 hover:text-emerald-700" data-testid={`subcat-up-${s.id}`}><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === subs.length - 1} className="text-gray-400 disabled:opacity-30 hover:text-emerald-700" data-testid={`subcat-down-${s.id}`}><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  {editingId === s.id ? (
                    <>
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && rename(s.id)} className="flex-1 bg-white h-9" data-testid={`subcat-edit-input-${s.id}`} autoFocus />
                      <button onClick={() => rename(s.id)} className="text-emerald-700" data-testid={`subcat-save-${s.id}`}><Check className="w-5 h-5" /></button>
                      <button onClick={() => setEditingId(null)} className="text-gray-400"><X className="w-5 h-5" /></button>
                    </>
                  ) : (
                    <>
                      <div className="w-11 h-11 rounded-lg bg-white overflow-hidden flex items-center justify-center border shrink-0">
                        {s.image ? <img src={s.image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-4 h-4 text-gray-300" />}
                      </div>
                      <span className="flex-1 font-medium text-gray-700">{s.name}</span>
                      <input type="file" accept="image/*" hidden id={`subcat-img-${s.id}`}
                        onChange={(e) => setImage(s.id, e.target.files?.[0])} data-testid={`subcat-image-input-${s.id}`} />
                      <button onClick={() => document.getElementById(`subcat-img-${s.id}`).click()} className="text-gray-500 hover:text-emerald-700" data-testid={`subcat-image-${s.id}`}>
                        {uploadingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                      </button>
                      <button onClick={() => { setEditingId(s.id); setEditName(s.name); }} className="text-gray-500 hover:text-emerald-700" data-testid={`subcat-rename-${s.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(s.id)} className="text-red-500 hover:text-red-700" data-testid={`subcat-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
